import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import { Link, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../hooks';
import { getCandidatesByZohoIds, getZohoUploadCandidatesCount } from '../../lib/api';
import { planHasAtsIntegration } from '../../lib/utils/planEntitlements';
import { getCandidateProfilePath } from '../../lib/utils/candidateRoutes';
import {
  ZOHO_UPLOAD_FIELDS,
  dbColumnsFromUploadFieldsState,
  getDefaultUploadFieldsState,
  getDefaultZohoUploadDbColumns,
  hasSelectedUploadFields,
  isZohoUploadFieldsUnset,
  normalizeZohoUploadDbColumns,
  uploadFieldsStateFromDbColumns,
} from '../../lib/utils/zohoUploadFields';
import Button from '../../components/ui/Button';
import ZohoSetupGuide from './components/ZohoSetupGuide';
import { Checkbox } from '../../components/ui/Checkbox';
import Tooltip from '../../components/ui/Tooltip';

const normalizeZohoIdList = (ids) => (
  (Array.isArray(ids) ? ids : [])
    .map((id) => String(id ?? '').trim())
    .filter(Boolean)
);

const resolveZohoCandidates = (zohoIds, byZohoId) => (
  zohoIds.map((zohoId) => {
    const match = byZohoId.get(zohoId);
    return match || { id: null, fullName: null, zohoId };
  })
);

const ZOHO_AUTH_BASE = 'https://accounts.zoho.eu/oauth/v2/auth';
const ZOHO_CLIENT_ID = '1000.Z3B7YH8Y8LH696L2S3KYNBF27NGLRI';
const ZOHO_REDIRECT_URI = 'https://n8n.hireportal.ai/webhook/zoho_login';
const ZOHO_SCOPE = 'ZohoRecruit.modules.ALL';
const ZOHO_SYNC_URL = 'https://n8n.hireportal.ai/webhook/run_zoho_sync';
const ZOHO_IMPORT_URL = 'https://n8n.hireportal.ai/webhook/zoho_import';
const ZOHO_UPDATE_URL = 'https://n8n.hireportal.ai/webhook/zoho_update';

const ConnectATS = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile, loading: profileLoading, refetch, updateProfile } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notice, setNotice] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadableCount, setUploadableCount] = useState(0);
  const [uploadFields, setUploadFields] = useState(getDefaultUploadFieldsState);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const consentParagraphs = t('import.consentParagraphs', { returnObjects: true });
  const consentText = Array.isArray(consentParagraphs) ? consentParagraphs : [];
  const seedDefaultsRef = useRef(false);
  const plan = profile?.subscription_plan?.toLowerCase() || null;
  const hasAtsIntegration = planHasAtsIntegration(plan);
  const isZohoConnected = Boolean(profile?.zoho_refresh_token);

  useEffect(() => {
    const zohoResult = searchParams.get('zoho');
    if (!zohoResult) return;

    if (zohoResult === 'success') {
      setNotice({ type: 'success', message: t('connectAts.zohoSuccess') });
      seedDefaultsRef.current = true;
      refetch?.({ silent: true });
    } else {
      setNotice({ type: 'error', message: t('connectAts.zohoError') });
    }

    searchParams.delete('zoho');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, refetch, t]);

  // Hydrate checkbox UI from profile, and seed defaults after successful Zoho connect.
  // Empty array is an intentional "no fields" choice — do not reset to defaults.
  useEffect(() => {
    if (!isZohoConnected || !profile) return;

    if (!isZohoUploadFieldsUnset(profile.zoho_upload_fields)) {
      const normalized = normalizeZohoUploadDbColumns(profile.zoho_upload_fields);
      setUploadFields(uploadFieldsStateFromDbColumns(normalized));
      seedDefaultsRef.current = false;

      // Migrate legacy column names (e.g. current_title → current_position).
      const raw = (Array.isArray(profile.zoho_upload_fields) ? profile.zoho_upload_fields : [])
        .map((value) => String(value ?? '').trim())
        .filter(Boolean);
      const needsMigration = raw.length !== normalized.length
        || raw.some((value, index) => value !== normalized[index]);
      if (needsMigration) {
        updateProfile?.({ zoho_upload_fields: normalized }).catch((err) => {
          console.error('Failed to migrate Zoho upload fields:', err);
        });
      }
      return;
    }

    setUploadFields(getDefaultUploadFieldsState());

    if (!seedDefaultsRef.current) return;

    seedDefaultsRef.current = false;
    const defaults = getDefaultZohoUploadDbColumns();
    updateProfile?.({ zoho_upload_fields: defaults }).catch((err) => {
      console.error('Failed to seed Zoho upload fields:', err);
    });
  }, [isZohoConnected, profile, profile?.zoho_upload_fields, updateProfile]);

  useEffect(() => {
    if (!user?.id || !isZohoConnected) {
      setUploadableCount(0);
      return;
    }

    let cancelled = false;
    getZohoUploadCandidatesCount(user.id)
      .then((count) => {
        if (!cancelled) setUploadableCount(count);
      })
      .catch((err) => {
        console.error('Failed to count Zoho upload candidates:', err);
        if (!cancelled) setUploadableCount(0);
      });

    return () => { cancelled = true; };
  }, [user?.id, isZohoConnected, importResult]);

  const handleSyncCandidates = async () => {
    if (!user?.id || syncing || importing || uploading) return;
    setNotice(null);
    setSyncPreview(null);
    setImportResult(null);
    setUploadResult(null);
    setSyncing(true);
    try {
      const response = await fetch(ZOHO_SYNC_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });
      if (!response.ok) {
        throw new Error(t('connectAts.syncRequestFailed', { status: response.status }));
      }
      const data = await response.json();
      const allItems = Number(data?.all_items ?? 0);
      const correctItems = Number(data?.correct_items ?? 0);
      const remaining = Math.max(0, Number(data?.remaining ?? 0));
      const willImport = Math.min(correctItems, remaining);
      setSyncPreview({ allItems, correctItems, remaining, willImport });
    } catch (err) {
      setNotice({ type: 'error', message: err?.message || t('connectAts.syncFailed') });
    } finally {
      setSyncing(false);
    }
  };

  const handleImportCandidates = async () => {
    if (!user?.id || importing || uploading) return;
    setNotice(null);
    setImportResult(null);
    setImporting(true);
    try {
      const response = await fetch(ZOHO_IMPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });

      // Success is determined by HTTP status (200–299).
      if (response.status >= 200 && response.status < 300) {
        let result = null;
        try {
          const data = await response.json();
          result = {
            total: Number(data?.total ?? 0),
            added: Number(data?.added ?? 0),
            updated: Number(data?.updated ?? 0),
            skipped: Number(data?.skipped ?? 0),
            failed: Number(data?.failed ?? 0),
          };
        } catch {
          // Empty or non-JSON body — still treat as success.
        }
        setImportResult(result);
        setSyncPreview(null);
        await refetch?.({ silent: true });
        return;
      }

      throw new Error(t('connectAts.importFailedStatus', { status: response.status }));
    } catch (err) {
      setNotice({ type: 'error', message: err?.message || t('connectAts.importFailed') });
    } finally {
      setImporting(false);
    }
  };

  const canUpdateInZoho = hasSelectedUploadFields(uploadFields);

  const handleUploadToZoho = async () => {
    if (!user?.id || uploading || syncing || importing || !canUpdateInZoho) return;
    setNotice(null);
    setUploadResult(null);
    setUploading(true);
    try {
      const response = await fetch(ZOHO_UPDATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (response.status >= 200 && response.status < 300) {
        let data = null;
        try {
          data = await response.json();
        } catch {
          // Empty or non-JSON body.
        }

        const skippedIds = normalizeZohoIdList(data?.skipped_ids);
        const failedIds = normalizeZohoIdList(data?.failed_ids);
        let skippedCandidates = [];
        let failedCandidates = [];

        if (skippedIds.length > 0 || failedIds.length > 0) {
          try {
            const byZohoId = await getCandidatesByZohoIds(user.id, [...skippedIds, ...failedIds]);
            skippedCandidates = resolveZohoCandidates(skippedIds, byZohoId);
            failedCandidates = resolveZohoCandidates(failedIds, byZohoId);
          } catch (lookupErr) {
            console.error('Failed to resolve Zoho candidate ids:', lookupErr);
            skippedCandidates = resolveZohoCandidates(skippedIds, new Map());
            failedCandidates = resolveZohoCandidates(failedIds, new Map());
          }
        }

        setUploadResult({
          total: Number(data?.total ?? uploadableCount),
          updated: Number(data?.updated ?? data?.added ?? 0),
          skipped: Number(data?.skipped ?? skippedIds.length),
          failed: Number(data?.failed ?? failedIds.length),
          skippedCandidates,
          failedCandidates,
        });
        return;
      }

      throw new Error(t('connectAts.uploadFailedStatus', { status: response.status }));
    } catch (err) {
      setNotice({ type: 'error', message: err?.message || t('connectAts.uploadFailed') });
    } finally {
      setUploading(false);
    }
  };

  const handleConnectZoho = () => {
    if (!user?.id || !consentAccepted) return;
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: ZOHO_CLIENT_ID,
      scope: ZOHO_SCOPE,
      redirect_uri: ZOHO_REDIRECT_URI,
      access_type: 'offline',
      prompt: 'consent',
      state: user.id,
    });
    window.location.href = `${ZOHO_AUTH_BASE}?${params.toString()}`;
  };

  if (profileLoading && !profile) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('connectAts.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!hasAtsIntegration) {
    return (
      <MainLayout>
        <div className="container mx-auto px-4 py-6 max-w-5xl">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{t('connectAts.title')}</h1>
              <p className="text-muted-foreground">
                Integrate with your Applicant Tracking System for seamless workflow management.
              </p>
            </div>
            <ZohoSetupGuide />
          </div>

          <div className="rounded-xl border border-border bg-card p-6 md:p-8 text-center">
            <p className="text-foreground font-medium mb-2">{t('connectAts.upgradeTitle')}</p>
            <p className="text-muted-foreground mb-6">
              {t('connectAts.upgradeBody')}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button asChild>
                <Link to="/billing" className="inline-flex items-center gap-2">
                  <Icon name="CreditCard" size={18} />
                  {t('common.upgradePlan')}
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/dashboard" className="inline-flex items-center gap-2">
                  <Icon name="ArrowLeft" size={18} />
                  {t('connectAts.backToDashboard')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">{t('connectAts.title')}</h1>
            <p className="text-muted-foreground">
              {t('connectAts.subtitle')}
            </p>
          </div>
          <ZohoSetupGuide />
        </div>

        <div className="rounded-xl border border-border bg-card p-6 md:p-8">
          {notice && (
            <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
              notice.type === 'success'
                ? 'border-success/30 bg-success/10 text-success'
                : 'border-destructive/30 bg-destructive/10 text-destructive'
            }`}>
              {notice.message}
            </div>
          )}
          <div className="flex items-center gap-3 mb-6">
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${
              isZohoConnected ? 'bg-success/10' : 'bg-muted'
            }`}>
              <Icon
                name={isZohoConnected ? 'CheckCircle2' : 'Link'}
                size={24}
                className={isZohoConnected ? 'text-success' : 'text-muted-foreground'}
              />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground">Zoho Recruit</p>
              <p className={`text-sm ${isZohoConnected ? 'text-success' : 'text-muted-foreground'}`}>
                {isZohoConnected ? t('connectAts.connected') : t('connectAts.notConnected')}
              </p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-6">
            {t('connectAts.authHint')}
          </p>

          <Checkbox
            id="ats-consent"
            className="items-start rounded-lg border border-border bg-background p-4 mb-6"
            checked={consentAccepted}
            onChange={(event) => setConsentAccepted(Boolean(event?.target?.checked))}
            label={(
              <span className="block space-y-2 text-sm font-normal leading-relaxed text-foreground">
                {consentText.map((paragraph) => (
                  <span key={paragraph} className="block">{paragraph}</span>
                ))}
              </span>
            )}
          />

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" asChild>
              <Link to="/dashboard" className="inline-flex items-center gap-2">
                <Icon name="ArrowLeft" size={18} />
                {t('connectAts.backToDashboard')}
              </Link>
            </Button>
            <Button
              onClick={handleConnectZoho}
              disabled={!consentAccepted}
              className="inline-flex items-center gap-2"
            >
              <Icon name="Link" size={18} />
              {isZohoConnected ? t('connectAts.reconnectZoho') : t('connectAts.connectZoho')}
            </Button>
          </div>
        </div>

        {isZohoConnected && (
          <div className="mt-6 rounded-xl border border-border bg-card p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-base font-semibold text-foreground mb-1">{t('connectAts.syncTitle')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('connectAts.syncDescription')}
                </p>
                <p className="mt-2 text-sm text-warning">
                  {t('connectAts.syncWarning')}
                </p>
              </div>
              <Button
                type="button"
                onClick={handleSyncCandidates}
                loading={syncing}
                disabled={syncing || importing || uploading}
                className="inline-flex items-center gap-2 flex-shrink-0"
              >
                <Icon name="RefreshCw" size={18} />
                {syncing ? t('connectAts.checking') : t('connectAts.syncButton')}
              </Button>
            </div>

            {syncPreview && (
              <div className="mt-5 rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <ul className="text-sm text-foreground space-y-1 list-none">
                    <li>
                      {t('connectAts.foundInZoho')}:{' '}
                      <span className="font-semibold tabular-nums">{syncPreview.allItems}</span>
                    </li>
                    <li>
                      {t('connectAts.correct')}:{' '}
                      <span className="font-semibold tabular-nums">{syncPreview.correctItems}</span>
                    </li>
                    <li>
                      {t('connectAts.willBeImported')}:{' '}
                      <span className="font-semibold tabular-nums">{syncPreview.willImport}</span>
                    </li>
                  </ul>
                  {!importing && (
                    <div className="flex flex-wrap gap-2 flex-shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSyncPreview(null)}
                        className="border-destructive/50 text-destructive hover:!bg-destructive hover:!text-destructive-foreground hover:!border-destructive"
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleImportCandidates}
                        disabled={syncPreview.willImport <= 0}
                        className="inline-flex items-center gap-2"
                      >
                        <Icon name="Download" size={18} />
                        {t('connectAts.import')}
                      </Button>
                    </div>
                  )}
                </div>

                {importing && (
                  <div className="flex items-center gap-3 py-1">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">{t('connectAts.importing')}</p>
                  </div>
                )}
              </div>
            )}

            {importResult && (
              <div className="mt-5 rounded-lg border border-border bg-muted/30 px-4 py-3">
                <p className="text-sm font-semibold text-foreground mb-2">{t('connectAts.importFinished')}</p>
                <ul className="text-sm space-y-1 list-none">
                  <li className="text-foreground">
                    {t('connectAts.total')}:{' '}
                    <span className="font-semibold tabular-nums">{importResult.total}</span>
                  </li>
                  <li className="text-success">
                    {t('connectAts.added')}:{' '}
                    <span className="font-semibold tabular-nums">{importResult.added}</span>
                  </li>
                  <li className="text-blue-600">
                    {t('connectAts.updated')}:{' '}
                    <span className="font-semibold tabular-nums">{importResult.updated}</span>
                  </li>
                  <li className="text-warning">
                    {t('connectAts.skipped')}:{' '}
                    <span className="font-semibold tabular-nums">{importResult.skipped}</span>
                  </li>
                  <li className="text-destructive">
                    {t('connectAts.failed')}:{' '}
                    <span className="font-semibold tabular-nums">{importResult.failed}</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}

        {isZohoConnected && uploadableCount > 0 && (
          <div className="mt-6 rounded-xl border border-border bg-card p-6 md:p-8">
            <p className="text-base font-semibold text-foreground mb-1">
              {t('connectAts.uploadFieldsTitle')}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {t('connectAts.uploadFieldsDescription')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ZOHO_UPLOAD_FIELDS.map((field) => (
                <Checkbox
                  key={field.id}
                  id={`zoho-upload-field-${field.id}`}
                  label={t(field.labelKey)}
                  checked={Boolean(uploadFields[field.id])}
                  disabled={uploading || syncing || importing}
                  onChange={(event) => {
                    const checked = event?.target?.checked ?? false;
                    setUploadFields((prev) => {
                      const next = { ...prev, [field.id]: checked };
                      const columns = dbColumnsFromUploadFieldsState(next);
                      updateProfile?.({ zoho_upload_fields: columns }).catch((err) => {
                        console.error('Failed to save Zoho upload fields:', err);
                      });
                      return next;
                    });
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {isZohoConnected && uploadableCount > 0 && (
          <div className="mt-6 rounded-xl border border-border bg-card p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-base font-semibold text-foreground mb-1">{t('connectAts.uploadTitle')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('connectAts.uploadDescription')}
                </p>
                <p className="mt-2 text-sm text-foreground">
                  {t('connectAts.uploadCount', { count: uploadableCount })}
                </p>
              </div>
              {!canUpdateInZoho ? (
                <Tooltip content={t('connectAts.uploadNoFieldsTooltip')} align="end" size="md">
                  <span className="inline-flex cursor-not-allowed flex-shrink-0">
                    <Button
                      type="button"
                      disabled
                      className="inline-flex items-center gap-2"
                    >
                      <Icon name="SquarePen" size={18} />
                      {t('connectAts.uploadButton')}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  type="button"
                  onClick={handleUploadToZoho}
                  loading={uploading}
                  disabled={uploading || syncing || importing}
                  className="inline-flex items-center gap-2 flex-shrink-0"
                >
                  <Icon name="SquarePen" size={18} />
                  {uploading ? t('connectAts.uploading') : t('connectAts.uploadButton')}
                </Button>
              )}
            </div>

            {uploading && (
              <div className="mt-5 flex items-center gap-3 py-1">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent flex-shrink-0" />
                <p className="text-sm text-muted-foreground">{t('connectAts.uploadingProgress')}</p>
              </div>
            )}

            {uploadResult && !uploading && (
              <div className="mt-5 rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">{t('connectAts.uploadFinished')}</p>
                  <ul className="text-sm space-y-1 list-none">
                    <li className="text-foreground">
                      {t('connectAts.total')}:{' '}
                      <span className="font-semibold tabular-nums">{uploadResult.total}</span>
                    </li>
                    <li className="text-success">
                      {t('connectAts.updated')}:{' '}
                      <span className="font-semibold tabular-nums">{uploadResult.updated}</span>
                    </li>
                    <li className="text-warning">
                      {t('connectAts.skipped')}:{' '}
                      <span className="font-semibold tabular-nums">{uploadResult.skipped}</span>
                    </li>
                    <li className="text-destructive">
                      {t('connectAts.failed')}:{' '}
                      <span className="font-semibold tabular-nums">{uploadResult.failed}</span>
                    </li>
                  </ul>
                </div>

                {uploadResult.skippedCandidates?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-warning mb-1.5">{t('connectAts.skippedCandidates')}</p>
                    <ul className="text-sm space-y-1 list-none">
                      {uploadResult.skippedCandidates.map((candidate) => (
                        <li key={`skipped-${candidate.zohoId}`}>
                          {candidate.id ? (
                            <Link
                              to={getCandidateProfilePath(candidate.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary underline"
                            >
                              {candidate.fullName}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">
                              {t('connectAts.notFound', { id: candidate.zohoId })}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {uploadResult.failedCandidates?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-destructive mb-1.5">{t('connectAts.failedCandidates')}</p>
                    <ul className="text-sm space-y-1 list-none">
                      {uploadResult.failedCandidates.map((candidate) => (
                        <li key={`failed-${candidate.zohoId}`}>
                          {candidate.id ? (
                            <Link
                              to={getCandidateProfilePath(candidate.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary underline"
                            >
                              {candidate.fullName}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">
                              Zoho ID {candidate.zohoId} (not found in HirePortal)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ConnectATS;
